import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import Link from '@/routing/Link'
import { isContextMaterialIndexingInProgress } from '@digital-twins/shared-types'
import { KnowledgeDocumentWithStages } from '@/lib/api'
import { CardOverlayDeleteButton } from '@/components/ui/CardOverlayDeleteButton'
import { Badge } from '@/components/ui/badge'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { KnowledgeStageFailureNotice } from './KnowledgeStageFailureNotice'
import { indexingStageLabel } from './KnowledgeIndexingBadge'
import {
  documentCoverUrl,
  documentDescription,
  documentTitle,
  documentTypeLabel,
  displayKnowledgeTypeLabel,
  facetValue,
  facetValues,
  fileKindLabel,
  marketDisplayEntries,
} from './libraryHelpers'

interface KnowledgeLibraryCardProps {
  doc: KnowledgeDocumentWithStages
  deleting: boolean
  onDelete: (token: string) => void
  onDocumentUpdated?: (doc: KnowledgeDocumentWithStages) => void
  /** Opens the document in the library's overlay pane instead of navigating to a new page. */
  onOpen?: (token: string) => void
}

export function KnowledgeLibraryCard({
  doc,
  deleting,
  onDelete,
  onDocumentUpdated,
  onOpen,
}: KnowledgeLibraryCardProps) {
  const { t } = useTranslation('app')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const marketValues = facetValues(doc.facets, 'market')
  const year = facetValue(doc.facets, 'year')
  const brand = facetValue(doc.facets, 'brand')
  const typeLabel = displayKnowledgeTypeLabel(documentTypeLabel(doc))
  const kind = fileKindLabel(doc)
  const inProgress = isContextMaterialIndexingInProgress(doc.indexingStatus)
  // Markets live in the footer, never as pills: a document can carry a dozen
  // of them and the pill row has to stay one line above the title.
  const markets = marketDisplayEntries(marketValues)
  const marketLabels = markets.map((market) => market.label)
  const coverSrc = documentCoverUrl(doc)
  const description = documentDescription(doc)
  const stageLabel = indexingStageLabel(doc.indexingStatus)
  const blockedReason = t('library.opensWhenReady', { stage: stageLabel })

  const marketMeta =
    markets.length === 0 ? null : markets.length === 1 ? (
      <span
        className="inline-flex items-center gap-1"
        data-testid={`knowledge-doc-market-${doc.token}`}
      >
        {markets[0].flag ? (
          <span aria-hidden="true" className="text-[13px] leading-none">
            {markets[0].flag}
          </span>
        ) : (
          <Globe className="h-3 w-3 shrink-0" aria-hidden="true" />
        )}
        {markets[0].label}
      </span>
    ) : (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          {/* asChild keeps this trigger a <span>: the whole card is already a
              <button> or an <a>, so a nested button would be invalid markup. */}
          <TooltipTrigger asChild>
            <span
              className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
              aria-label={t('library.marketsAria', { list: marketLabels.join(', ') })}
              data-testid={`knowledge-doc-market-${doc.token}`}
            >
              <Globe className="h-3 w-3 shrink-0" aria-hidden="true" />
              {t('library.marketCount', { count: markets.length })}
            </span>
          </TooltipTrigger>
          <TooltipContent
            className="max-w-[240px] text-[12px] normal-case tracking-normal"
            data-testid={`knowledge-doc-market-tooltip-${doc.token}`}
          >
            {marketLabels.join(', ')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )

  const cardBody = (
    <>
        <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-muted">
          <img
            src={coverSrc}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-top"
            data-testid={`knowledge-doc-cover-${doc.token}`}
          />
          {inProgress ? (
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-black/45 px-3 py-2 text-[12px] text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              {stageLabel}
            </div>
          ) : null}
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-5 pb-5 pt-4">
          {/* One line, never wrapped: a long owner name truncates instead of
              pushing the title down and breaking the grid's title baseline. */}
          <div className="flex min-h-[24px] min-w-0 items-center gap-1.5 overflow-hidden">
            <Badge
              variant="muted"
              className="shrink-0 rounded-full border-0 px-2.5 py-0.5 text-[12px] font-medium"
              data-testid="knowledge-doc-pill-type"
            >
              {typeLabel}
            </Badge>
            {brand ? (
              <Badge
                variant="accent"
                title={brand}
                className="min-w-0 overflow-hidden"
                data-testid="knowledge-doc-pill-brand"
              >
                {/* The ellipsis has to live on a child: text-overflow does
                    nothing on the Badge's own inline-flex box. */}
                <span className="truncate">{brand}</span>
              </Badge>
            ) : null}
            {doc.isScannedPdf ? (
              <Badge
                variant="warning"
                className="shrink-0 rounded-full border-0 px-2.5 py-0.5 text-[12px] font-medium"
                data-testid="knowledge-scanned-badge"
              >
                {t('library.noSearchableText')}
              </Badge>
            ) : null}
          </div>
          <Heading
            level={3}
            className="line-clamp-2 min-h-[2.75rem] leading-snug"
          >
            {documentTitle(doc)}
          </Heading>
          {description ? (
            <Text
              size="sm"
              color="muted"
              className="line-clamp-2 text-[13px] leading-snug"
              data-testid={`knowledge-doc-card-desc-${doc.token}`}
            >
              {description}
            </Text>
          ) : null}
          <div
            className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] uppercase tracking-[0.04em] text-muted-foreground"
            data-testid={`knowledge-doc-card-meta-${doc.token}`}
          >
            <span>{kind}</span>
            {marketMeta ? (
              <>
                <span aria-hidden="true">·</span>
                {marketMeta}
              </>
            ) : null}
            {year ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{year}</span>
              </>
            ) : null}
          </div>
        </div>
    </>
  )

  const surfaceClassName =
    'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card-inset text-left dark:bg-card'

  return (
    <div
      className="group relative flex h-full min-w-0 flex-col"
      data-testid={`knowledge-doc-card-wrap-${doc.token}`}
    >
      {inProgress ? (
        <div
          aria-disabled="true"
          title={blockedReason}
          data-testid={`knowledge-doc-card-${doc.token}`}
          className={`${surfaceClassName} cursor-not-allowed opacity-75`}
        >
          {cardBody}
        </div>
      ) : onOpen ? (
        <button
          type="button"
          data-testid={`knowledge-doc-card-${doc.token}`}
          className={`${surfaceClassName} transition-all duration-180 hover:-translate-y-0.5 hover:shadow-md`}
          onClick={() => onOpen(doc.token)}
        >
          {cardBody}
        </button>
      ) : (
        <Link
          href={`/knowledge/${doc.token}`}
          data-testid={`knowledge-doc-card-${doc.token}`}
          className={`${surfaceClassName} transition-all duration-180 hover:-translate-y-0.5 hover:shadow-md`}
        >
          {cardBody}
        </Link>
      )}

      <KnowledgeStageFailureNotice
        doc={doc}
        variant="compact"
        className="mt-2"
        onDocumentUpdated={onDocumentUpdated}
      />

      <div className="absolute right-3 top-3 z-20 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <CardOverlayDeleteButton
          ariaLabel={t('library.deleteAria', {
            name: doc.originalFilename || t('library.documentFallback'),
          })}
          dataTestId={`knowledge-doc-delete-${doc.token}`}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setConfirmDelete(true)
          }}
        />
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('library.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('library.deleteBody', {
                name: doc.originalFilename || t('library.untitled'),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{t('library.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              data-testid={`knowledge-doc-delete-confirm-${doc.token}`}
              onClick={() => onDelete(doc.token)}
            >
              {deleting ? t('library.deleting') : t('library.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
